import React, { Dispatch, useEffect, useRef, useState } from 'react'
import { iReChargeProduct } from '.'
import { ProductVariant, Product } from '../../../util/typings';
import Money from '../../Money';

interface Props {
  reChargeData: iReChargeProduct
  product: Product
  currentVariant: ProductVariant
  setFrequencySelected: Dispatch<React.SetStateAction<string>>
  setSellingPlanId: Dispatch<React.SetStateAction<number>>
  setIsAuto: Dispatch<React.SetStateAction<boolean>>
}

const ReChargeForm: React.FunctionComponent<Props> = ({ setSellingPlanId, product, reChargeData, currentVariant, setFrequencySelected, setIsAuto }): JSX.Element => {
  const [initialPrice, setInitialPrice] = useState(0)
  const [reChargePrice, setReChargePrice] = useState(0)
  const [checked, setIsChecked] = useState(false)
  const reChargeOptions = useRef<Array<HTMLDivElement>>([])
  const { originalToHiddenVariantMap, shippingIntervalFrequency, strings } = reChargeData;
  const durationFrequency = shippingIntervalFrequency.split(',').map(freq => parseInt(freq));
  const [frequencyIndex, setFrequencyIndex] = useState(0)
  const [rechargeProductData, setRechargeProductData] = useState() as any
  const [discountRate, setDiscountRate] = useState(0 as number) 

  // Issue
  useEffect(() => {
    if (currentVariant) {
      setInitialPrice(currentVariant.price)
      if(originalToHiddenVariantMap && currentVariant && currentVariant.id) {
        const variant = originalToHiddenVariantMap[currentVariant.id];
        console.log('setting reChargePrice 1', variant)
        setReChargePrice(parseFloat(currentVariant.selling_plan_allocations[0]?.price))
      }
    } 
  }, [currentVariant])

  //On first load set the first option as the duration. This is to protect against the customer not selecting a duration
  useEffect(() => {
    setFrequencySelected(durationFrequency[0].toString())

    const getData = async () => {
      const data = await window['ReChargeWidget'].api.fetchWidgetData()
      const activeProduct = data.products.find(rechargeProductPlan => {
        const key = parseInt(Object.keys(rechargeProductPlan)[0])
        return key === product.id
      })
      if(activeProduct) {
        setRechargeProductData(activeProduct[product.id])
        setReChargePrice(activeProduct[product.id].variants[0]?.prices.discounted_price)
        setDiscountRate(activeProduct[product.id].selling_plan_groups[0].selling_plans[0].price_adjustments_value)
        if(activeProduct[product.id].selling_plan_groups) {
          // mutliple plans
          setSellingPlanId(activeProduct[product.id].selling_plan_groups[0].selling_plans[0].selling_plan_id)
        } else {
          // single plan
          setSellingPlanId(activeProduct[product.id].selling_plan_groups[0].selling_plans[0].selling_plan_id)
        }
      }
    }
    getData()
  }, [product])

  const selectionHandler = (e: React.ChangeEvent<HTMLInputElement>, auto: boolean) => {
    setIsAuto(auto);
    const target = e.currentTarget;
    setIsChecked(!checked)
    target.parentElement === reChargeOptions.current[1] ? setIsChecked(true) : setIsChecked(false)
  }

  if(!currentVariant || !rechargeProductData) {
    return <></>
  } 

  return (
    <div className="rc_container" style={{display: 'block'}}>
      <div className={`rc_block rc_block__type rc_block__type__autodeliver  rc_block__type ${checked ? 'rc_block__type--active' : ''}`} ref={el => reChargeOptions.current[1] = el}>
        <input type="radio" name="purchase_type" id="rc_purchase_type_autodeliver" value="autodeliver" defaultChecked={checked} className="rc_radio rc_radio__autodeliver" onChange={(e) => {
          selectionHandler(e, true)
          const getRechargeVariant = rechargeProductData.variants.find(vrnt => vrnt.id === currentVariant.id)
          setReChargePrice(getRechargeVariant.prices?.discounted_price)
        }
        } />
				
        <label htmlFor="rc_purchase_type_autodeliver" className="rc_label rc_label__autodeliver">
          <span dangerouslySetInnerHTML={{__html: strings.subAndSave }} /><span className="rc_label__discount" aria-label="subscription discount">{discountRate}%</span>
          <span id="rc_price_autodeliver" className="rc_price rc_price__autodeliver" aria-label="recurring price">{<Money withCurrency={false} amount={reChargePrice} />}</span>
        </label>				
        <div id="rc_autodeliver_options" className={`rc_block rc_block__type__options ${checked ? '' : 'hide'}`}>
          <label htmlFor="rc_shipping_interval_frequency" className="rc_label rc_label__deliver_every" aria-label="Subscription frequency options">
            <select name="properties[shipping_interval_frequency]" id="rc_shipping_interval_frequency" className="rc_select rc_select__frequency styled replaced"
              defaultValue={
              rechargeProductData?.selling_plan_groups
                ?.flatMap((group) => group.selling_plans)
                ?.find((plan) => plan.selling_plan_name === 'Delivery every 4 weeks')?.selling_plan_id
              }
              onChange={(e) => {
                setFrequencyIndex(parseInt(e.target.value))
                setFrequencySelected(e.target.value);

                const tempSelectedRechargeVariant = rechargeProductData.variants.find(v => v.id === currentVariant.id)
                if(tempSelectedRechargeVariant) {
                  setReChargePrice(tempSelectedRechargeVariant.selling_plan_allocations[frequencyIndex]?.discounted_price)
                } else {
                  setReChargePrice(tempSelectedRechargeVariant?.prices.discounted_price)
                }

                if(tempSelectedRechargeVariant?.selling_plan_allocations) {
                  setSellingPlanId([parseInt(e.target.value)][0])
                } else {
                  setSellingPlanId(tempSelectedRechargeVariant.selling_plan_groups[parseInt(e.target.value)].selling_plans[0].selling_plan_id)
                }
              }
              }>
              {rechargeProductData && rechargeProductData.selling_plan_groups ? (
                rechargeProductData.selling_plan_groups.map((group, groupIndex) => (
                  group.selling_plans.map((plan, planIndex) => {
                    return (
                      <option key={`${groupIndex}-${planIndex}`} value={plan.selling_plan_id} data-plan-name={plan.selling_plan_name}>
                        {plan.selling_plan_name}
                      </option>
                    );
                  })
                ))
              ) : null}
            </select>
          </label>
        </div>
      </div>
      <div id="rc_radio_options" role="radiogroup" aria-label="Purchase options">
        <div className={`rc_block rc_block__type rc_block__type__onetime ${!checked ? 'rc_block__type--active' : ''}`} ref={el => reChargeOptions.current[0] = el}>
          <input type="radio" id="rc_purchase_type_onetime" name="purchase_type" value="onetime" defaultChecked={!checked} className="rc_radio rc_radio__onetime" onChange={(e) => selectionHandler(e, false)}/>
          <label htmlFor="rc_purchase_type_onetime" className="rc_label rc_label__onetime">
            {strings.oneTimePurchase}
            <span id="rc_price_onetime" className="rc_price rc_price__onetime" aria-label="original price">{<Money withCurrency={false} amount={initialPrice} />}</span>
          </label>
        </div>
      </div>
    </div>
  )
}

export default ReChargeForm